/**
 * This module manages the users model of the API
 *
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */

import api from '@/api'
import {
    VUEX_USER_ADMIN_UPDATE,
    VUEX_USER_CREATE_START,
    VUEX_USER_CREATE_REQUEST,
    VUEX_USER_CREATE_REQUEST_FAILURE,
    VUEX_USER_CREATE_REQUEST_SUCCESS,
    VUEX_USER_GET_REQUEST,
    VUEX_USER_GET_REQUEST_FAILURE,
    VUEX_USER_GET_REQUEST_SUCCESS,
    VUEX_USERS_GET_USERS_BY_ROLE_AND_DEPARTMENT,
    VUEX_USERS_GET_USERS_BY_ROLE_AND_DEPARTMENT_SUCCESS,
    VUEX_USERS_GET_USERS_BY_ROLE,
    VUEX_USERS_GET_USERS_BY_ROLE_SUCCESS,
    VUEX_USERS_GET_USERS_BY_ROLE_ACTIVE,
    VUEX_USERS_GET_USERS_BY_ROLE_ACTIVE_SUCCESS,
    VUEX_USERS_GET_USERS_BY_ROLE_NO_PENDING,
    VUEX_USERS_GET_USERS_BY_ROLE_NO_PENDING_SUCCESS,
    VUEX_USERS_GET_USERS_BY_EMPLOYEE_TYPE,
    VUEX_USERS_GET_USERS_BY_EMPLOYEE_TYPE_SUCCESS,
    VUEX_USER_GOTO_CREATE_ROUTE,
    VUEX_USER_GOTO_UPDATE_ROUTE,
    VUEX_USER_SELF_UPDATE,
    VUEX_USER_UPDATE_END,
    VUEX_USER_UPDATE_START,
    VUEX_USER_UPDATE_PROP,
    VUEX_USER_UPDATE_PROP_REQUEST,
    VUEX_USER_UPDATE_PROP_REQUEST_FAILURE,
    VUEX_USER_UPDATE_PROP_REQUEST_SUCCESS,

    VUEX_USERS_GET_REQUEST,
    VUEX_USERS_GET_REQUEST_FAILURE,
    VUEX_USERS_GET_REQUEST_SUCCESS,
    VUEX_USERS_SYNC_PAGINATION,
    VUEX_USERS_APPLY_DEEPSTATE,

    VUEX_USER_CREATE_ROLE_REQUEST,
    VUEX_USER_CREATE_ROLE_REQUEST_SUCCESS,
    VUEX_USER_CREATE_ROLE_REQUEST_FAILURE,
    VUEX_USER_DELETE_ROLE_REQUEST,
    VUEX_USER_DELETE_ROLE_REQUEST_SUCCESS,
    VUEX_USER_DELETE_ROLE_REQUEST_FAILURE,
    VUEX_USERS_EMPLOYEES_PAGINATION_EXIT,

    //filters
    VUEX_USERS_SET_FILTER_TOGGLER,
    VUEX_USERS_APPLY_FILTERS,
    VUEX_USERS_CLEAR_FILTERS,
    VUEX_USERS_APPLY_DEPARTMENT_FILTER,
    VUEX_USERS_APPLY_DEPARTMENT_ROLE_FILTER,
    VUEX_USERS_APPLY_ROLE_FILTER,
    VUEX_USERS_APPLY_STATUS_FILTER,
    VUEX_USERS_APPLY_TYPE_FILTER,
    VUEX_USERS_APPLY_ACCESS_FILTER,

    VUEX_USERS_EXIT_USERS,

    //SEARCH
    VUEX_USERS_GET_SEARCH,
    VUEX_USERS_GET_SEARCH_SUCCESS,
    VUEX_USERS_GET_SEARCH_FAILURE,
    VUEX_USERS_CLEAR_SEARCH_DATA,
    VUEX_USERS_UPDATE_SEARCH_TERM,

    // ATTACHMENTS
    VUEX_USER_ATTACH_FILE,
    VUEX_USERS_HANDLE_FILE_NOTIFICATION,
    VUEX_USER_REFRESH_CURRENT_USER_AVATAR,

    //BATCH REASSING
    VUEX_USER_BATCH_REASSIGN,
    VUEX_USER_BATCH_REASSIGN_OPEN,
    VUEX_USER_BATCH_REASSIGN_CLOSE,
} from '@/store/constants/users'

import {
    VUEX_UI_SHOW_USER_OVERLAY,
    VUEX_UI_HIDE_USER_OVERLAY,
    VUEX_UI_HIDE_USER_SEARCH,
    VUEX_UI_HIDE_USER_SEARCH_MOBILE,
    VUEX_UI_HIDE_RIGHT_DRAWER,
    VUEX_UI_HIDE_MOBILE_DRAWER_RIGHT
} from '@/store/constants/ui'


import {
    VUEX_AUTH_LOGOUT,
    VUEX_AUTH_REFRESH_CURRENT_USER_AVATAR,
} from '@/store/constants/auth'


import {
    VUEX_SNACKBAR_SHOW
} from '@/store/constants/snackbar'


import {VUEX_PERSISTENCE_SET_VALUE} from '@/store/constants/persistence'

import cloneDeep from 'lodash/cloneDeep'
import router from '@/router'
import store from '@/store'
import Vue from 'vue'


const createUserTemplate = {
    access_type: null,
    email: '',
    employee_type: 1,
    first_name: '',
    last_name: '',
    password: '',
    permissions: [],
    roles: [],
    requestor_departments: [],
    status: null,
    title: '',
    access_reason: ''
}

/**
 * Users module state
 * @const
 */
const state = {
    users: [],
    usersLoaded: false,
    usersByRoleAndDepartment: [],
    usersByRole: {},
    usersByRoleActive: {},
    usersByRoleNoPending: {},

    usersByRoleIsLoading: false,
    usersByRoleActiveIsLoading: false,
    usersByRoleNoPendingIsLoading: false,
    usersByEmployeeType: {},
    user: {},
    updateUser: {},
    appliedFilters: [],
    errors: [],
    createUserCancelPrompt: false,
    pagination: {
        rowsPerPage: 10,
        page: 1,
        totalItems: 10,
        descending: false,
    },
    isAdminUpdate: false,
    isUserSelfUpdate: false,
    isCreatingUser: false,
    isUpdatingUser: false,
    isSavingUser: false,
    isUserCreated: false,

    // SPECIAL
    filterToggler: '',

    //filter related
    usersMeta: [],

    // State: JOB SEARCH
    searchData: [],                   // Search data from API
    searchTerm: null,                 // Search term entered by user
    searchLoading: false,             // If are currently being looked up

    //batch reassign
    reassignOverlayToggler: 'close',
}

/**
 * Routing module getters
 * @const
 */
const getters = {

    getUserId: (state) => () => {
        if (state.user)
            return state.user.id

        return null
    },

    getUser: (state) => () => {
        return state.user
    },

    getUsers: (state) => () => {
        if (state.users)
            return state.users

        return []
    },

    getUserStatuses: (state) => {
        let statuses = []

        const userPropStatuses = store.state.properties.userStatuses
        const ACTIVE = SILA_USER_STATUS_ACTIVE
        const ARCHIVED = SILA_USER_STATUS_ARCHIVED
        const DENIED = SILA_USER_STATUS_DENIED
        const DISABLED = SILA_USER_STATUS_DISABLED
        const PENDING = SILA_USER_STATUS_PENDING
        const REQUESTED = SILA_USER_STATUS_REQUESTED

        switch (parseInt(state.user.status, 10)) {
            case ACTIVE:
                statuses = [ACTIVE, DISABLED, ARCHIVED]
                break;
            case DENIED:
                statuses = [DENIED, ACTIVE, DISABLED, ARCHIVED]
                break;
            case DISABLED:
                statuses = [DISABLED, ACTIVE, ARCHIVED]
                break;
            case ARCHIVED:
                statuses = [ARCHIVED, ACTIVE]
                break;
            case REQUESTED:
                statuses = [REQUESTED, ACTIVE, DENIED]
                break;
            default:
                statuses = []
        }

        return userPropStatuses.filter(status => statuses.indexOf(status.value) > -1)
    },

    isUserInternalAccessType: (state) => {
        if (state.user) {
            return state.user.access_type === SILA_USER_ACCESS_TYPE_INTERNAL
        } else {
            return false
        }
    },

    /**
     *
     * @param state
     * @returns {function(*): string}
     */
    getUsersFilterCount: (state) => (obj) => {
        var id = obj.id;
        var ret = '';

        id = id.split(':').join('_');

        if (state.usersMeta &&
            state.usersMeta.hasOwnProperty('filter_counts') &&
            state.usersMeta.filter_counts.hasOwnProperty(id)) {
            ret = state.usersMeta.filter_counts.hasOwnProperty(id) ? state.usersMeta.filter_counts[id] : ret;
        }
        return String(ret);
    },

    //search data
    getUserSearchData: (state) => () => state.searchData,
}

/**
 * Users module action definitions
 * @const
 */
const actions = {
    //called from routing
    [VUEX_USERS_EXIT_USERS]: async ({ state, commit, dispatch }, key) => {
        //!!PERSISTENCE
        var dsobj = {}
        dsobj.appliedFilters = cloneDeep(state.appliedFilters)
        dsobj.pagination = cloneDeep(state.pagination)
        var obj = {
            key: key,
            value: dsobj
        }
        await dispatch(VUEX_PERSISTENCE_SET_VALUE, obj)

        //clear out search if open
        dispatch(VUEX_UI_HIDE_USER_SEARCH)
        dispatch(VUEX_UI_HIDE_USER_SEARCH_MOBILE)


        //NOW LEAVE
        await commit(VUEX_USERS_EXIT_USERS)
    },


    //get request
    [VUEX_USERS_GET_REQUEST]: async ({ commit, getters }, extraParams = {}) => {
        commit(VUEX_USERS_GET_REQUEST, extraParams)
        if (getters.isUserInternalAccessType === true) {
            extraParams.accessType = '${SILA_USER_ACCESS_TYPE_INTERNAL}'
        }

        let params = Object.assign(
            {
                page: state.pagination.page,
                per_page: state.pagination.rowsPerPage,
            },
            extraParams
        )
        if (state.pagination && state.pagination.sortBy) {
            params['sort'] = state.pagination.descending ? '-' + state.pagination.sortBy : state.pagination.sortBy
        }

        for (var f in state.appliedFilters) {
            var key = state.appliedFilters[f]['filterOn']
            var val = state.appliedFilters[f]['value']
            if (!params.hasOwnProperty(key)) {
                params[key] = val
            } else {
                params[key] = params[key] + ',' + val
            }
        }

        //explicity add the icon
        params['include'] = 'avatar'

        await api.get(`/users/`, { params }).then((response) => {
            commit(VUEX_USERS_GET_REQUEST_SUCCESS, response.data)
        }).catch((err) => {
            commit(VUEX_USERS_GET_REQUEST_FAILURE)
            console.error(err)
        })
    },

    [VUEX_USERS_APPLY_DEEPSTATE]: async ({ getters, commit, dispatch, rootState }, payload) => {
        await commit(VUEX_USERS_APPLY_DEEPSTATE, payload.deepstate)
        await dispatch(VUEX_USERS_GET_REQUEST, payload.extraParams)
    },


    [VUEX_USERS_SYNC_PAGINATION]: async ({ commit, dispatch }, payload) => {
        //
        // TODO: BL
        // This is a hack to get around the fact that the set/get on the users grid calls this on
        // its load b/c the vuetify table changes pagination when it gets mounted (defaults to
        // sort by first sortable column in header). I could block it on the grid component, but
        // it seems illogical to keep syncing pagination if there's no users anyway.
        //
        if (state.usersLoaded === false) {
            return
        }

        await commit(VUEX_USERS_SYNC_PAGINATION, payload.pagination)
        await dispatch(VUEX_USERS_GET_REQUEST, payload.extraParams)
    },

    /**
     * Get Users by staff type
     * Params :
     user id
     * @function
     */
    [VUEX_USERS_GET_USERS_BY_EMPLOYEE_TYPE]: ({ state, commit }, payload) => {
        commit(VUEX_USERS_GET_USERS_BY_EMPLOYEE_TYPE)

        let roles = [
            SILA_USER_EMPLOYEE_TYPE_STAFF,
            SILA_USER_EMPLOYEE_TYPE_FREELANCE
        ]

        if (payload && roles.indexOf(payload) > -1) {
            var role = payload
            api.get(`/users-employee-type?et=${role}&include=avatar`).then((response) => {
                commit(VUEX_USERS_GET_USERS_BY_EMPLOYEE_TYPE_SUCCESS, { role: role, data: response.data.data })
            }).catch((err) => {
                console.error(err)
            })
        } else {
            roles.forEach(role => {
                if (!state.usersByEmployeeType[role]) {
                    api.get(`/users-employee-type?et=${role}&include=avatar`).then((response) => {
                        commit(VUEX_USERS_GET_USERS_BY_EMPLOYEE_TYPE_SUCCESS, { role: role, data: response.data.data })
                    }).catch((err) => {
                        console.error(err)
                    })
                }
            })
        }


    },


    /**
     * Get Users by role
     * Params :
     user id
     * @function
     */
    [VUEX_USERS_GET_USERS_BY_ROLE]: ({ state, commit }, payload) => {
        commit(VUEX_USERS_GET_USERS_BY_ROLE)

        let roles = [
            SILA_ROLE_JOBS_REQUESTOR,
            SILA_ROLE_JOBS_EXECUTIVE,
            SILA_ROLE_JOBS_PROJECT_MANAGER,
            SILA_ROLE_JOBS_PRINT_PROJECT_MANAGER,
            SILA_ROLE_JOBS_PREMEDIA_PROJECT_MANAGER,
            SILA_ROLE_JOBS_ART_DIRECTOR,
            SILA_ROLE_JOBS_CREATIVE
        ]

        if (payload && roles.indexOf(payload) > -1) {
            var role = payload
            api.get(`/users?role=${role}&include=roles,avatar,active_jobs_count`).then((response) => {
                commit(VUEX_USERS_GET_USERS_BY_ROLE_SUCCESS, { role: role, data: response.data.data })
            }).catch((err) => {
                console.error(err)
            })
        } else {
            roles.forEach(role => {
                if (!state.usersByRole[role]) {
                    api.get(`/users?role=${role}&include=roles,avatar,active_jobs_count`).then((response) => {
                        commit(VUEX_USERS_GET_USERS_BY_ROLE_SUCCESS, { role: role, data: response.data.data })
                    }).catch((err) => {
                        console.error(err)
                    })
                }
            })
        }
    },

    [VUEX_USERS_GET_USERS_BY_ROLE_AND_DEPARTMENT]: async ({ state, commit }, payload) => {
        commit(VUEX_USERS_GET_USERS_BY_ROLE_AND_DEPARTMENT)

        //we only need active here
        await api.get(`/users?state=active&role=${payload.role}&department=${payload.department}&per_page=99999`).then((response) => {
            commit(VUEX_USERS_GET_USERS_BY_ROLE_AND_DEPARTMENT_SUCCESS, response.data.data)
        }).catch((err) => {
            console.error(err)
        })
    },

    [VUEX_USERS_GET_USERS_BY_ROLE_ACTIVE]: ({ state, commit }, payload) => {
        commit(VUEX_USERS_GET_USERS_BY_ROLE_ACTIVE)

        let roles = [
            SILA_ROLE_JOBS_REQUESTOR,
            SILA_ROLE_JOBS_EXECUTIVE,
            SILA_ROLE_JOBS_PROJECT_MANAGER,
            SILA_ROLE_JOBS_PRINT_PROJECT_MANAGER,
            SILA_ROLE_JOBS_PREMEDIA_PROJECT_MANAGER,
            SILA_ROLE_JOBS_ART_DIRECTOR,
            SILA_ROLE_JOBS_CREATIVE
        ]

        if (payload && roles.indexOf(payload) > -1) {
            var role = payload
            api.get(`/users?role=${role}&include=roles,avatar,active_creative_jobs_count,active_jobs_count&state=active`).then((response) => {
                commit(VUEX_USERS_GET_USERS_BY_ROLE_ACTIVE_SUCCESS, { role: role, data: response.data.data })
            }).catch((err) => {
                console.error(err)
            })
        } else {
            roles.forEach(role => {
                if (!state.usersByRoleActive[role]) {
                    api.get(`/users?role=${role}&per_page=99999&include=roles,avatar,active_creative_jobs_count,active_jobs_count&state=active`).then((response) => {
                        commit(VUEX_USERS_GET_USERS_BY_ROLE_ACTIVE_SUCCESS, { role: role, data: response.data.data })
                    }).catch((err) => {
                        console.error(err)
                    })
                }
            })
        }


    },

    [VUEX_USERS_GET_USERS_BY_ROLE_NO_PENDING]: ({ state, commit }, payload) => {
        commit(VUEX_USERS_GET_USERS_BY_ROLE_NO_PENDING)

        let roles = [
            SILA_ROLE_JOBS_REQUESTOR,
            SILA_ROLE_JOBS_EXECUTIVE,
            SILA_ROLE_JOBS_PROJECT_MANAGER,
            SILA_ROLE_JOBS_PRINT_PROJECT_MANAGER,
            SILA_ROLE_JOBS_PREMEDIA_PROJECT_MANAGER,
            SILA_ROLE_JOBS_ART_DIRECTOR,
            SILA_ROLE_JOBS_CREATIVE
        ]

        if (payload && roles.indexOf(payload) > -1) {
            var role = payload
            api.get(`/users?role=${role}&include=roles,avatar,active_jobs_count&state=active,archived`).then((response) => {
                commit(VUEX_USERS_GET_USERS_BY_ROLE_NO_PENDING_SUCCESS, { role: role, data: response.data.data })
            }).catch((err) => {
                console.error(err)
            })
        } else {
            roles.forEach(role => {
                if (!state.usersByRoleNoPending[role]) {
                    api.get(`/users?role=${role}&per_page=99999&include=roles,avatar,active_jobs_count&state=active,archived`).then((response) => {
                        commit(VUEX_USERS_GET_USERS_BY_ROLE_NO_PENDING_SUCCESS, {
                            role: role,
                            data: response.data.data
                        })
                    }).catch((err) => {
                        console.error(err)
                    })
                }
            })
        }


    },


    /**
     * Get User
     * Params :
     user id
     * @function
     */
    [VUEX_USER_GET_REQUEST]: ({ commit, dispatch }, params) => {
        commit(VUEX_USER_GET_REQUEST)

        let urlTemplate = `users/${params.userId}`

        if (params.hasOwnProperty('include')) {
            if (typeof params.include === 'string') {
                urlTemplate = `users/${params.userId}?include=${params.include}`
            } else if (typeof params.include === 'object') { // hopefully an array
                urlTemplate = `users/${params.userId}?include=${params.include.join(",")}`
            }
        }

        api.get(urlTemplate).then((response) => {
            commit(VUEX_USER_GET_REQUEST_SUCCESS, response.data.data)


            //GLOBAL ACTION PROMPT: Pass array of optional states to watch
            var stateChangeWatchers = [
                ['users', 'user']
            ]
            dispatch('VUEX_GLOBAL_ACTION_PROMPT_SET_STATE_CHANGE_WATCHERS', { stateChangeWatchers: stateChangeWatchers })


        }).catch((err) => {
            console.error(err)
            commit(VUEX_USER_GET_REQUEST_FAILURE)
        })
    },

    /**
     * Create User
     * Params :
     user
     * @function
     */
    [VUEX_USER_CREATE_REQUEST]: async ({ commit, dispatch, getters }, payload) => {
        commit(VUEX_USER_CREATE_REQUEST)
        let userData = Object.assign({}, payload)

        await api.post(`users/?source_of_request=admin`, userData).then(async (response) => {
            commit(VUEX_USER_CREATE_REQUEST_SUCCESS, response.data)

            if (userData.roles && userData.roles.length)
                await dispatch(VUEX_USER_CREATE_ROLE_REQUEST, userData.roles)

            // activate immediately if an admin user making the request
            if (getters.hasManageUsersPerms) {
                //await dispatch(VUEX_USER_UPDATE_PROP_REQUEST, { transition: 'activate'})

                //Show snackbar for success
                dispatch(VUEX_SNACKBAR_SHOW, {
                    description_text: "Success! This user has been created.",
                    button_text: "",
                    timeout: 4000,
                    button_dispatch: "",
                    button_dispatch_payload: {},
                })
            }

            await dispatch(VUEX_USER_UPDATE_END)


        }).catch((err) => {
            commit(VUEX_USER_CREATE_REQUEST_FAILURE, err.response.data.errors)
            console.error(err)
        })
    },

    /**
     * Create User Role
     * Params :
     user
     * @function
     */
    [VUEX_USER_CREATE_ROLE_REQUEST]: async ({ state, commit }, roleIds) => {
        commit(VUEX_USER_CREATE_ROLE_REQUEST)

        await api.post(`users/${state.user.id}/roles`, { roles: roleIds }).then((response) => {
            commit(VUEX_USER_CREATE_ROLE_REQUEST_SUCCESS)
        }).catch((err) => {
            commit(VUEX_USER_CREATE_ROLE_REQUEST_FAILURE)
            console.error(err)
        })
    },

    /**
     * Delete User Role
     * Params :
     user
     * @function
     */
    [VUEX_USER_DELETE_ROLE_REQUEST]: async ({ state, commit }, roleIds) => {
        commit(VUEX_USER_DELETE_ROLE_REQUEST)

        await api.delete(`users/${state.user.id}/roles`, { data: { roles: roleIds } }).then((response) => {
            commit(VUEX_USER_DELETE_ROLE_REQUEST_SUCCESS)
        }).catch((err) => {
            commit(VUEX_USER_DELETE_ROLE_REQUEST_FAILURE)
            console.error(err)
        })
    },

    /**
     * Update User Prop
     * Params :
     user prop
     * @function
     */
    [VUEX_USER_UPDATE_PROP_REQUEST]: async ({ state, commit, dispatch, getters, rootState }, payload) => {
        commit(VUEX_USER_UPDATE_PROP_REQUEST)

        const isExternalUserType = getters.isUserInternalAccessType === false
        const userPropStatuses = rootState.properties.userStatuses
        let transition;
        let userData = Object.assign({}, payload)


        if (userData.status) {
            const statuses = {
                [SILA_USER_STATUS_ACTIVE]: 'activate',
                [SILA_USER_STATUS_DENIED]: 'deny',
                [SILA_USER_STATUS_DISABLED]: 'disable',
                [SILA_USER_STATUS_ARCHIVED]: 'archive',
            }
            transition = statuses[parseInt(userData.status)]
        }
        if (transition) {
            userData.transition = transition
        }

        if (isExternalUserType) {
            userData.roles = [SILA_ROLE_JOBS_REQUESTOR, SILA_ROLE_ORGANIZATION_EXTERNAL]
        }

        await api.patch(`users/${state.user.id}`, userData).then(async () => {
            commit(VUEX_USER_UPDATE_PROP_REQUEST_SUCCESS)
            const hasNewRoles = userData.roles && userData.roles.length

            if (isExternalUserType || hasNewRoles) {
                await dispatch(VUEX_USER_DELETE_ROLE_REQUEST, userData.oldRoles)
                await dispatch(VUEX_USER_CREATE_ROLE_REQUEST, userData.roles)
            }
            dispatch(VUEX_USER_UPDATE_END)
        }).catch((err) => {
            commit(VUEX_USER_UPDATE_PROP_REQUEST_FAILURE, err.response.data.errors)
            console.error(err)
        })
    },

    [VUEX_USER_GOTO_UPDATE_ROUTE]: async ({ commit }, userId) => {
        commit(VUEX_USER_GOTO_UPDATE_ROUTE)

        router.push({ name: 'admin-user-edit', params: { userId: userId } })
    },

    [VUEX_USER_GOTO_CREATE_ROUTE]: async ({ commit }) => {
        commit(VUEX_USER_GOTO_CREATE_ROUTE)

        router.push({ name: 'admin-user-create' })
    },

    [VUEX_USER_CREATE_START]: async ({ commit, dispatch }) => {
        commit(VUEX_USER_CREATE_START)
        await dispatch(VUEX_UI_SHOW_USER_OVERLAY)
    },

    [VUEX_USER_ADMIN_UPDATE]: async ({ commit }) => {
        commit(VUEX_USER_ADMIN_UPDATE)
    },

    [VUEX_USER_SELF_UPDATE]: async ({ commit }) => {
        commit(VUEX_USER_SELF_UPDATE)

        //APPCUES
        window.Appcues.track("Open My Account");
    },

    [VUEX_USER_UPDATE_START]: async ({ commit, dispatch }, { isAdminUpdate, isSelfUpdate }) => {
        commit(VUEX_USER_UPDATE_START)

        if (isAdminUpdate)
            await dispatch(VUEX_USER_ADMIN_UPDATE)

        if (isSelfUpdate)
            await dispatch(VUEX_USER_SELF_UPDATE)

        await dispatch(VUEX_UI_SHOW_USER_OVERLAY)
    },

    [VUEX_USER_UPDATE_END]: async ({ rootState, state, commit, dispatch }) => {
        commit(VUEX_USER_UPDATE_END)
        await dispatch(VUEX_UI_HIDE_USER_OVERLAY)

        if (state.user.id)
            dispatch(VUEX_USER_GET_REQUEST, state.user.id)

        if (rootState.routing.routeHistory.length <= 1) {
            //this means there was no back - it was deep-linked in, so take them to the jobs-manage page, because everyone has access to that
            router.push({ name: 'admin-users' })
        } else {
            router.go(-1)
        }

    },

    [VUEX_USER_UPDATE_PROP]: async ({ commit }, prop) => {
        commit(VUEX_USER_UPDATE_PROP, prop)
    },

    [VUEX_USERS_EMPLOYEES_PAGINATION_EXIT]: async ({ commit }) => {
        commit(VUEX_USERS_EMPLOYEES_PAGINATION_EXIT)
    },


    ////////////////////////////////////////////////////////////////
    //FILTERS
    ////////////////////////////////////////////////////////////////
    [VUEX_USERS_APPLY_FILTERS]: async ({ commit, dispatch }, filterItem) => {
        await commit(VUEX_USERS_APPLY_FILTERS, filterItem)
        await dispatch(VUEX_USERS_GET_REQUEST)
    },
    [VUEX_USERS_APPLY_ROLE_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'role'
        await dispatch(VUEX_USERS_APPLY_FILTERS, newFilterItem)
    },
    [VUEX_USERS_APPLY_DEPARTMENT_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'department'
        await dispatch(VUEX_USERS_APPLY_FILTERS, newFilterItem)
    },
    [VUEX_USERS_APPLY_DEPARTMENT_ROLE_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'departmentRole'
        await dispatch(VUEX_USERS_APPLY_FILTERS, newFilterItem)
    },
    [VUEX_USERS_APPLY_TYPE_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'employeeType'
        await dispatch(VUEX_USERS_APPLY_FILTERS, newFilterItem)
    },
    [VUEX_USERS_APPLY_ACCESS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'access'
        await dispatch(VUEX_USERS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_USERS_APPLY_STATUS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'state'
        await dispatch(VUEX_USERS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_USERS_CLEAR_FILTERS]: async ({ commit }) => {
        await commit(VUEX_USERS_CLEAR_FILTERS)
    },


    /**
     * THIS FILTER TOGGLER IS USED TO SYNC UP BUTTON DISPLAY STATES
     */
    [VUEX_USERS_SET_FILTER_TOGGLER]: ({ commit, state }, obj) => {
        var oldValue = state.filterToggler
        var newValue = obj.value

        // always refresh quick
        if (newValue !== 'manual') {
            newValue = 'quick' + Date.now()
        }

        if (oldValue !== 'manual' && newValue === 'manual') {
            //Probably don't do this anymore now that we have me button, but may add it again
            //commit(VUEX_JOBS_REMOVE_APPLIED_FILTER_BY_NAME, "userMeInRole")
        }

        commit(VUEX_USERS_SET_FILTER_TOGGLER, newValue)
    },


    // SEARCH
    [VUEX_USERS_UPDATE_SEARCH_TERM]: async ({ commit, dispatch }, payload) => {
        if (!payload) return
        dispatch(VUEX_USERS_GET_SEARCH, payload)
        commit(VUEX_USERS_GET_SEARCH)
    },
    [VUEX_USERS_GET_SEARCH]: async ({ commit, dispatch }, payload) => {
        let params = {
            'q': payload.q,
            'includeArchive': payload.includeArchive
        }

        await api.get(`/users-search/`, { params }).then(async (response) => {
            commit(VUEX_USERS_GET_SEARCH_SUCCESS, response)
        })
    },
    [VUEX_USERS_CLEAR_SEARCH_DATA]: async ({ commit, dispatch }, payload) => {
        commit(VUEX_USERS_CLEAR_SEARCH_DATA)
    },


    /**
     * File Attachment
     */

    // Action: Attach a file to the user
    [VUEX_USER_ATTACH_FILE]: async ({ dispatch }, payload) => {
        api.post(`/attachments`, {
            file_id: payload.id,
            model_id: payload.attach_to.model_id,
            model: payload.attach_to.model,
            note: payload.note || null,
        }).then(async (response) => {
            // dispatch(VUEX_USER_GET_ATTACHMENTS, payload.job_id)
        }).catch((err) => {
            console.error(err)
        })
    },


    // Action: Handle file notification
    [VUEX_USERS_HANDLE_FILE_NOTIFICATION]: async ({ rootState, commit }, payload) => {
        let timeout = 5000
        if (payload.upload_status !== SILA_FILE_UPLOAD_STATUS_SUCCESS) {
            timeout = 100
        }

        if (payload.uploader_id === rootState.auth.currentUser.id) {
            // Current logged in user
            setTimeout(() => { // Have to await arrival at S3
                api.get(`auth/user`).then((user_response) => {
                    let user = user_response.data.data
                    commit(VUEX_AUTH_REFRESH_CURRENT_USER_AVATAR, user)
                })
            }, timeout)
        } else if (rootState.ui.userOverlay.openState === true) {
            // Current editing user
            setTimeout(() => { // Have to await arrival at S3
                api.get(`users/${user.id}?include=avatar`).then((user_response) => {
                    let user = user_response.data.data
                    commit(VUEX_USER_REFRESH_CURRENT_USER_AVATAR, user)
                })
            }, timeout)
        }
    },

    // batch re-assigned
    // has a test mode that leaves the dialogue open
    [VUEX_USER_BATCH_REASSIGN]: async ({ commit, dispatch }, payload) => {
        let testMode = parseInt(payload.test_mode_only)
        if (testMode !== 1) {
            commit(VUEX_USER_BATCH_REASSIGN_CLOSE, {})
        }

        await api.post(`/users/${payload.user_id}/batchreassign/`, payload).then((response) => {
            if (testMode !== 1) {
                dispatch(VUEX_USER_BATCH_REASSIGN_CLOSE)
            }

            //success message
            let msg = " job position"
            if (response.data.count !== 1) {
                msg += "s"
            }
            let finalmsg = ''

            if (testMode === 1) {
                finalmsg = "Test Result: " + response.data.count + msg + " would have been updated."
            } else {
                if (response.data.count === 0) {
                    finalmsg = "No job positions found to update."
                } else {
                    finalmsg = "Success! " + response.data.count + msg + " updated."
                }
            }

            dispatch(VUEX_SNACKBAR_SHOW, {
                description_text: finalmsg,
                button_text: "",
                timeout: 4000,
                button_dispatch: "",
                button_dispatch_payload: {},
            })
        }).catch((err) => {
            dispatch(VUEX_USER_BATCH_REASSIGN_CLOSE)

            //Show snackbar for success
            dispatch(VUEX_SNACKBAR_SHOW, {
                description_text: "Error! Please notify support if this problem reoccurs.",
                button_text: "",
                timeout: 4000,
                button_dispatch: "",
                button_dispatch_payload: {},
            })
        })
    },

    [VUEX_USER_BATCH_REASSIGN_OPEN]: ({ commit }) => {
        commit(VUEX_USER_BATCH_REASSIGN_OPEN)
    },
    [VUEX_USER_BATCH_REASSIGN_CLOSE]: ({ commit }) => {
        commit(VUEX_USER_BATCH_REASSIGN_CLOSE)
    },


}

/**
 * Users module mutation definitions
 * @const
 */
const mutations = {
    [VUEX_USER_BATCH_REASSIGN_OPEN]: (state) => {
        state.reassignOverlayToggler = ''
    },
    [VUEX_USER_BATCH_REASSIGN_CLOSE]: (state) => {
        state.reassignOverlayToggler = 'close' + Date.now();
    },

    [VUEX_USER_GET_REQUEST]: () => {
    },


    [VUEX_USERS_GET_REQUEST]: (state) => {
        state.users = []
        state.usersLoaded = false
        state.pagination.totalItems = 0
    },

    [VUEX_USERS_GET_REQUEST_FAILURE]: () => {
    },

    [VUEX_USER_GET_REQUEST_FAILURE]: () => {
    },

    [VUEX_USER_GOTO_UPDATE_ROUTE]: () => {
    },

    [VUEX_USER_CREATE_ROLE_REQUEST]: () => {
    },

    [VUEX_USER_CREATE_ROLE_REQUEST_SUCCESS]: () => {
    },

    [VUEX_USER_CREATE_ROLE_REQUEST_FAILURE]: () => {
    },

    [VUEX_USER_DELETE_ROLE_REQUEST]: () => {
    },

    [VUEX_USER_DELETE_ROLE_REQUEST_SUCCESS]: () => {
    },

    [VUEX_USER_DELETE_ROLE_REQUEST_FAILURE]: () => {
    },

    [VUEX_USER_GOTO_CREATE_ROUTE]: () => {
    },

    [VUEX_USER_UPDATE_PROP_REQUEST]: (state) => {
        state.isSavingUser = true
    },

    [VUEX_USER_UPDATE_PROP_REQUEST_SUCCESS]: (state) => {
        state.isSavingUser = false
    },

    [VUEX_USER_UPDATE_PROP_REQUEST_FAILURE]: (state, payload) => {
        const errors = []

        Object.keys(payload).forEach(
            key => errors.push(payload[key])
        )

        state.errors = errors.join('')

        state.isSavingUser = false
    },

    [VUEX_USER_CREATE_REQUEST]: (state) => {
        state.isSavingUser = true
    },

    [VUEX_USER_CREATE_REQUEST_SUCCESS]: (state, payload) => {
        state.user = payload.data
        state.isSavingUser = false
    },

    [VUEX_USER_CREATE_REQUEST_FAILURE]: (state, payload) => {
        const errors = []

        Object.keys(payload).forEach(
            key => errors.push(payload[key])
        )

        state.errors = errors.join('')

        state.isSavingUser = false
    },

    [VUEX_USERS_GET_REQUEST_SUCCESS]: (state, payload) => {
        state.usersLoaded = true
        state.users = payload.data
        state.usersMeta = payload.meta
        if (payload.meta.filter_counts.grandtotal) {
            state.pagination.totalItems = payload.meta.filter_counts.grandtotal
        } else {
            state.pagination.totalItems = payload.meta.total
        }
        state.pagination.page = payload.meta.current_page
    },

    [VUEX_USER_GET_REQUEST_SUCCESS]: (state, payload) => {
        if (payload.requestor_departments === null) {
            payload.requestor_departments = []
        }

        state.updateUser = { oldRoles: payload.roles }
        state.user = Object.assign({}, payload)

        // populate user provided data if available. Meant for admin user update: stage for it all for PATCH
        if (payload.user_provided_data) {
            Object.assign(state.user, payload.user_provided_data)
            Object.assign(state.updateUser, payload.user_provided_data);
        }
        //except status is always crappy, so don't use the user_provided_data
        state.user.status = payload.status


    },

    [VUEX_USERS_GET_USERS_BY_EMPLOYEE_TYPE]: (state) => {
        state.usersByEmployeeTypeIsLoading = true
    },

    [VUEX_USERS_GET_USERS_BY_EMPLOYEE_TYPE_SUCCESS]: (state, { role, data }) => {
        Vue.set(state.usersByEmployeeType, role, data)
        state.usersByEmployeeTypeIsLoading = false
    },


    [VUEX_USERS_GET_USERS_BY_ROLE_AND_DEPARTMENT]: (state) => {
        state.usersByRoleAndDepartment = []
    },

    [VUEX_USERS_GET_USERS_BY_ROLE_AND_DEPARTMENT_SUCCESS]: (state, payload) => {
        state.usersByRoleAndDepartment = payload
    },

    [VUEX_USERS_GET_USERS_BY_ROLE]: (state) => {
        state.usersByRoleIsLoading = true
    },

    [VUEX_USERS_GET_USERS_BY_ROLE_SUCCESS]: (state, { role, data }) => {
        Vue.set(state.usersByRole, role, data)
        state.usersByRoleIsLoading = false
    },

    [VUEX_USERS_GET_USERS_BY_ROLE_ACTIVE]: (state) => {
        state.usersByRoleActiveIsLoading = true
    },

    [VUEX_USERS_GET_USERS_BY_ROLE_ACTIVE_SUCCESS]: (state, { role, data }) => {
        state.usersByRoleActiveIsLoading = false
        Vue.set(state.usersByRoleActive, role, data)
    },

    [VUEX_USERS_GET_USERS_BY_ROLE_NO_PENDING]: (state) => {
        state.usersByRoleNoPendingIsLoading = true
    },

    [VUEX_USERS_GET_USERS_BY_ROLE_NO_PENDING_SUCCESS]: (state, { role, data }) => {
        Vue.set(state.usersByRoleNoPending, role, data)
        state.usersByRoleNoPendingIsLoading = false
    },


    [VUEX_USERS_SYNC_PAGINATION]: (state, pagination) => {
        state.pagination = pagination
    },

    [VUEX_USER_UPDATE_END]: (state) => {
        state.isAdminUpdate = false
        state.isUserSelfUpdate = false
        state.isCreatingUser = false
        state.userJobRoles = {}
        state.errors = []
        state.user = {};
        state.updateUser = {};
    },

    [VUEX_USER_CREATE_START]: (state) => {
        state.user = Object.assign({}, createUserTemplate)
        state.isCreatingUser = true
        state.isUpdatingUser = false
    },

    [VUEX_USER_UPDATE_START]: (state) => {
        state.isUpdatingUser = true
        state.isCreatingUser = false
    },

    [VUEX_USER_ADMIN_UPDATE]: (state) => {
        state.isAdminUpdate = true
    },

    [VUEX_USER_SELF_UPDATE]: (state) => {
        state.isUserSelfUpdate = true
    },

    [VUEX_USER_UPDATE_PROP]: (state, prop) => {
        if (prop.access_type) {
            delete prop.roles
            delete prop.permissions
            delete prop.requestor_departments
            state.user.requestor_departments = []
            state.user.roles = []
            state.user.permissions = []
        }

        Vue.set(state, 'user', Object.assign({}, state.user, prop))

        if (state.isUpdatingUser) {
            Vue.set(state, 'updateUser', Object.assign({}, state.updateUser, prop))
        }
    },

    [VUEX_USERS_EMPLOYEES_PAGINATION_EXIT]: (state) => {
        //state.pagination.page = 1
        //state.pagination.sortBy = 'first_name,last_name'
        //state.pagination.descending = false
    },

    /**
     * On logout, clear
     */
    [VUEX_AUTH_LOGOUT]: (state) => {
        state.users = []
        state.usersLoaded = false
    },


    //
    // Filtering
    //
    [VUEX_USERS_APPLY_FILTERS]: (state, filterItem) => {
        let idx = state.appliedFilters.findIndex(x => x.value === filterItem.value && x.filterOn === filterItem.filterOn)
        if (idx > -1) {
            state.appliedFilters.splice(idx, 1) // remove
        } else {
            state.appliedFilters.push(filterItem) // add
        }
    },
    [VUEX_USERS_CLEAR_FILTERS]: (state) => {
        state.appliedFilters = []
    },


    // Sets the watched property that will toggle filter state
    [VUEX_USERS_SET_FILTER_TOGGLER]: (state, payload) => {
        state.filterToggler = payload
    },

    // SEARCH
    [VUEX_USERS_GET_SEARCH]: (state) => {
        state.searchLoading = true
    },

    [VUEX_USERS_GET_SEARCH_SUCCESS]: (state, payload) => {
        state.searchData = payload.data.data
        state.searchLoading = false
    },

    [VUEX_USERS_GET_SEARCH_FAILURE]: (state) => {
        state.searchLoading = false
    },

    [VUEX_USERS_CLEAR_SEARCH_DATA]: (state) => {
        state.searchData = []
        state.searchTerm = null
        state.searchLoading = false
    },

    [VUEX_USERS_EXIT_USERS]: (state, payload) => {
        state.users = []
        state.usersLoaded = false
        state.appliedFilters = []

        state.pagination.page = 1

    },


    [VUEX_USERS_APPLY_DEEPSTATE]: (state, payload) => {
        if (payload == undefined) {
            state.appliedFilters = []
            state.pagination = {
                rowsPerPage: 10,
                page: 1,
                totalItems: 10,
                descending: false,
            }
        } else {
            state.appliedFilters = payload.appliedFilters
            state.pagination = payload.pagination
        }
    },

    // Mutation: Refresh current user editing avatar
    [VUEX_USER_REFRESH_CURRENT_USER_AVATAR]: (state, payload) => {
        if (payload.id === state.user.id) {
            state.user.avatar = payload.avatar || null
            state.user.avatar_urls = payload.avatar_urls || {}
        }
    }
}

/**
 * The Users module of our Vuex store.
 * @module store/modules/users
 */
export default {
    state,
    getters,
    actions,
    mutations,
}
